import {
  ActionIcon,
  Table,
  Text,
  Button,
  Badge,
  Skeleton,
  Pagination,
  Select,
  Modal,
} from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import React, { useState, useEffect } from "react";
import {
  Edit,
  History,
  SortAscending,
  SortDescending,
} from "tabler-icons-react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUserData } from "../../redux/selectors";
import { apiWrapWithErrorWithData } from "../../utilities/apiHelpers";
import {
  getAgreementsList,
  getAgreementTypesApi,
} from "../../utilities/apis/agreements";
import { getBussinessUnit } from "../../utilities/apis/serviceProvider";
import {
  formatDate,
  formatIndianNumber,
  getStatusColor,
  getStatusText,
} from "../../utilities/utilities";
import { showNotification } from "@mantine/notifications";
import { draftAgreemntsApi } from "../../utilities/apis/agreements";
import { loadingStates } from "../../utilities/utilities";
import { formatISO } from "date-fns";

const DraftAgreements = () => {
  const navigate = useNavigate();
  const currentUser = useSelector(selectUserData) || {};

  const [businessunitList, setBusinessunitList] = useState([]);
  const [pageSize, setPageSize] = useState(10);

  const [listing, setListing] = useState({
    list: null,
    agreementsCount: null,
    loaded: false,
    sortBy: "createdAt",
    sortDirection: "desc",
    searchString: "",
    statusType: "",
    agreementType: null,
    location: null,
    startDate: null,
    endDate: null,
    partyName: "",
    businessunit: "",
  });

  const [agreementTypeOptions, setAgreementTypeOptions] = useState([]);
  const [uiConfigs, setUiConfigs] = useState({
    loading: loadingStates.NO_ACTIVE_REQUEST,
    page: 1,
  });
  const [transformedData, setTransformedData] = useState([]);

  const statusOptions = [
    { label: "DRAFT", value: "DRAFT" },
    { label: "CREATED", value: "CREATED" },
  ];

  useEffect(() => {
    const fetchBusinessUnits = async () => {
      try {
        const { data } = await getBussinessUnit();
        if (data) {
          setBusinessunitList(data.result || []);
        }
      } catch (error) {
        console.error("Error fetching business units:", error);
      }
    };
    fetchBusinessUnits();
    getAgreementTypes();
  }, []);

  useEffect(() => {
    if (businessunitList.length > 0) {
      setTransformedData(
        businessunitList.map((unit) => ({
          value: unit.id + "",
          label: unit.name,
        }))
      );
    }
  }, [businessunitList]);

  const handleKeyChange = (val) => {
    setListing({ ...listing, sortBy: val });
  };

  const toggleOrder = () => {
    setListing({
      ...listing,
      sortDirection: listing.sortDirection === "asc" ? "desc" : "asc",
    });
  };

  const getAgreementTypes = async () => {
    const resp = await apiWrapWithErrorWithData(draftAgreemntsApi());
    if (resp?.success && resp?.typesOfAgreement) {
      const uniqueValues = Array.from(new Set([...resp.typesOfAgreement]));
      const temp = uniqueValues.map((item) => ({ label: item, value: item }));
      setAgreementTypeOptions([...agreementTypeOptions, ...temp]);
    }
  };

  const fetchDraftAgreementsList = async ({ page }) => {
    let filterOptions = {};

    if (listing.startDate && listing.endDate) {
      filterOptions["createdAt"] = {
        gte: formatISO(listing.startDate),
        lte: formatISO(listing.endDate),
      };
    }

    setUiConfigs({
      ...uiConfigs,
      loading: loadingStates.LOADING,
    });

    const payload = {
      page: page ? page : uiConfigs.page,
      sortByOptions: {
        [listing.sortBy]: listing.sortDirection,
      },
      pageSize: pageSize,
      filterOptions: {
        ...filterOptions,
        status: { equals: "Draft" }, // Only fetch draft agreements
      },
    };

    // Add other filters if selected
    if (listing.agreementType) {
      payload.filterOptions.typeOfAgreement = { equals: listing.agreementType };
    }
    if (listing.businessunit) {
      payload.filterOptions.mapBusinessunit = { equals: listing.businessunit };
    }

    const response = await apiWrapWithErrorWithData(
      draftAgreemntsApi({ ...payload })
    );

    if (response?.success && response?.agreements) {
      setListing({
        ...listing,
        list: response?.agreements,
        agreementsCount: response.agreementsCount,
        loaded: true,
      });
    }

    setUiConfigs({
      ...uiConfigs,
      loading: loadingStates.NO_ACTIVE_REQUEST,
    });
  };

  useEffect(() => {
    fetchDraftAgreementsList({});
  }, [uiConfigs.page, pageSize]);

  const onContinueClick = (agreementId) => {
    navigate(`/app/agreements_manager/agreements/edit`, {
      state: {
        agreementId: agreementId,
      },
    });
  };

  const clearHandler = async () => {
    setListing({
      ...listing,
      searchString: "",
      agreementType: "",
      statusType: null,
      location: null,
      startDate: null,
      endDate: null,
      partyName: "",
      businessunit: "",
    });
    fetchDraftAgreementsList({ page: 1 });
  };

  return (
    <>
      <div className="flex flex-col">
        <div className="flex justify-between my-4">
          <Text size="xl" weight={600}>
            Draft Agreements
          </Text>
        </div>
      </div>

      <div className="flex w-full justify-between items-center">
        <div className="flex flex-col">
          <div className="flex flex-row-sm">
            <div className="flex items-center">
              <Select
                className="mx-2"
                placeholder="select contract type"
                value={listing.agreementType}
                onChange={(val) => {
                  setListing((prevState) => ({
                    ...prevState,
                    agreementType: val,
                  }));
                }}
                data={agreementTypeOptions}
                style={{ minWidth: "228px" }}
              />
              <Select
                className="mx-2"
                placeholder="select business unit"
                value={listing.businessunit}
                data={transformedData}
                onChange={(value) => {
                  setListing((prevState) => ({
                    ...prevState,
                    businessunit: value,
                  }));
                }}
                style={{ minWidth: "228px" }}
              />
            </div>
          </div>
        </div>
      </div>

      <br />

      <div className="flex w-full justify-between items-center">
        <div className="flex flex-col">
          <div className="flex flex-row">
            <div className="flex items-center">
              <DatePicker
                className="mx-2"
                value={listing.startDate}
                onChange={(val) =>
                  setListing((preState) => ({ ...preState, startDate: val }))
                }
                placeholder="created date (from)"
                minDate={
                  new Date(new Date().setMonth(new Date().getMonth() - 3))
                }
                style={{ minWidth: "228px" }}
              />
              <DatePicker
                className="mx-2"
                value={listing.endDate}
                onChange={(val) =>
                  setListing((preState) => ({ ...preState, endDate: val }))
                }
                placeholder="created date (To)"
                minDate={listing.startDate}
                disabled={listing.startDate ? false : true}
                style={{ minWidth: "228px" }}
              />
            </div>
          </div>
        </div>
      </div>

      <br />

      <Button
        className="mx-2"
        color="green"
        onClick={() => fetchDraftAgreementsList({ page: 1 })}
      >
        search
      </Button>
      <Button className="mx-2" color="red" onClick={clearHandler}>
        clear
      </Button>

      <div className="flex float-right">
        <div className="flex">
          <Select
            value={listing.sortBy}
            onChange={(val) => handleKeyChange(val)}
            data={[
              { label: "created at", value: "createdAt" },
              { label: "contract type", value: "typeOfAgreement" },
              { label: "ain", value: "id" },
            ]}
          />
          <ActionIcon className="mx-2" onClick={toggleOrder}>
            {listing.sortDirection === "asc" ? (
              <SortAscending size={32} />
            ) : (
              <SortDescending size={32} />
            )}
          </ActionIcon>
        </div>
      </div>

      <br />

      {uiConfigs.loading === loadingStates.LOADING && (
        <div className="flex flex-col">
          {Array.from({ length: 10 }).map((_, i) => (
            <Skeleton key={i} height={30} radius="md" className="my-1 w-full" />
          ))}
        </div>
      )}

      {listing.loaded &&
        uiConfigs.loading !== loadingStates.LOADING &&
        Boolean(listing.list?.length) && (
          <div
            className="flex flex-col"
            style={{
              overflowX: "scroll",
              marginTop: "15px",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            }}
          >
            <Table striped>
              <thead>
                <tr>
                  <th>AIN</th>
                  <th>Type</th>
                  <th>Business Unit</th>
                  <th>Status</th>
                  <th>Second Party</th>
                  <th>Annual Value (₹)</th>
                  <th>Contract Date</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {listing.list?.map((row, index) => (
                  <tr key={row.id}>
                    <td>{`agreement-${row.id}`}</td>
                    <td>{row.agreementData?.json?.typeOfAgreement || "NDA"}</td>
                    <td>{row.agreementData?.json?.location || "GURGAON"}</td>
                    <td>
                      <Badge color="yellow">Draft</Badge>
                    </td>
                    <td>
                      {row.agreementData?.json?.brandName || "tc"}
                      <br />
                      <span className="font-light">
                        {row.agreementData?.json?.secondParty?.name ||
                          "neolegal"}
                      </span>
                    </td>
                    <td>{formatIndianNumber(row.agreementValue || 10000)}</td>
                    <td>{formatDate(row.createdAt || "2025-05-21")}</td>
                    <td style={{ textAlign: "center" }}>
                      <Button
                        size="sm"
                        color="blue"
                        onClick={() => onContinueClick(row.id)}
                      >
                        Continue
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

      {listing.loaded &&
      uiConfigs.loading !== loadingStates.LOADING &&
      !listing.list?.length ? (
        <div
          className="flex justify-center items-center"
          style={{
            minHeight: "300px",
          }}
        >
          No draft agreements found
        </div>
      ) : null}

      {listing.agreementsCount ? (
        <div className="flex flex-row justify-center my-4">
          <Pagination
            page={uiConfigs.page}
            onChange={(page) => {
              setUiConfigs({
                ...uiConfigs,
                page,
              });
            }}
            total={Math.ceil(listing.agreementsCount / pageSize)}
          />
          <Text className="mx-4">select page size</Text>
          <Select
            className="w-20"
            data={["10", "20", "50"]}
            value={pageSize + ""}
            onChange={(val) => setPageSize(parseInt(val))}
          />
        </div>
      ) : null}
    </>
  );
};

export default DraftAgreements;
